/**
 * A person's gallery and its lightbox — docs/08 §3.4, §6.1, §6.4–6.5.
 *
 * The ordering rule is the whole design: dated photos oldest first, then the
 * undated ones under a heading of their own. "Nulls last" is easy to implement
 * and easy to get wrong in spirit — the tail of a long list is where things go
 * to be forgotten, so the undated group is *named*, counted, and put in front of
 * a reader who might know when it was.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { FuzzyDateParts, PersonSummary, PhotoView } from '@famtree/shared';
import { isFuzzyDateEmpty } from '@famtree/shared';
import { personsApi, photosApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { FuzzyDateField, FuzzyDateText } from '../../ui/FuzzyDateField.js';
import { PersonPicker } from '../people/PersonPicker.js';
import { personName, toPersonSummary } from '../people/labels.js';
import { problemMessage } from '../../ui/problem.js';
import { PhotoUploader } from './PhotoUploader.js';
import ui from '../../ui/components.module.css';
import styles from './photos.module.css';

export function PersonGallery({
  person,
  canWrite,
}: {
  person: PersonSummary;
  canWrite: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<PhotoView | null>(null);
  const [adding, setAdding] = useState(false);

  const gallery = useQuery({
    queryKey: ['gallery', person.id],
    queryFn: () => photosApi.gallery(person.id, { limit: 100 }),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['gallery', person.id] });
    await queryClient.invalidateQueries({ queryKey: ['person', person.id] });
  };

  if (gallery.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (gallery.isError) return <p className={ui.problem}>{problemMessage(gallery.error, t)}</p>;

  const { data, dated_count: datedCount, undated_count: undatedCount } = gallery.data;
  const dated = data.filter((photo) => photo.taken !== null);
  const undated = data.filter((photo) => photo.taken === null);

  return (
    <div>
      {canWrite && (
        <div className={ui.actions ?? ''}>
          <button type="button" className={ui.button} onClick={() => setAdding((value) => !value)}>
            {adding ? t('photos.hideUploader') : t('photos.addPhotos')}
          </button>
        </div>
      )}

      {adding && (
        <PhotoUploader
          defaultSubject={person}
          onUploaded={() => {
            void refresh();
          }}
        />
      )}

      {data.length === 0 && !adding && <p className={ui.muted}>{t('photos.emptyGallery')}</p>}

      {dated.length > 0 && <Tiles photos={dated} onOpen={setOpen} />}

      {undated.length > 0 && (
        <>
          {/* Named and counted, never a silent tail (docs/08 §6.1). */}
          <h3 className={styles.shelfTitle}>{t('photos.undatedShelf', { count: undatedCount })}</h3>
          <Tiles photos={undated} onOpen={setOpen} />
        </>
      )}

      {datedCount + undatedCount > data.length && (
        <p className={ui.muted}>{t('photos.moreAvailable')}</p>
      )}

      {open && (
        <PhotoLightbox
          photo={open}
          subjectPerson={person}
          onClose={() => setOpen(null)}
          onChanged={(photo) => {
            setOpen(photo);
            void refresh();
          }}
          onRemoved={() => {
            setOpen(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function Tiles({
  photos,
  onOpen,
}: {
  photos: PhotoView[];
  onOpen: (photo: PhotoView) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={styles.grid}>
      {photos.map((photo) => (
        <button
          key={photo.id}
          type="button"
          className={styles.tile}
          onClick={() => onOpen(photo)}
          aria-label={photo.caption ?? t('photos.untitled')}
        >
          {/* The 256 thumb, with the original as the fallback while the worker
              has not caught up — the `thumb_pending` case of docs/05 §8.3. */}
          <img
            src={photo.urls.thumb_256}
            alt={photo.caption ?? t('photos.untitled')}
            loading="lazy"
            onError={(event) => {
              const image = event.currentTarget;
              if (!image.dataset.fellBack) {
                image.dataset.fellBack = 'yes';
                image.src = photo.urls.original;
              }
            }}
          />
          {photo.caption && <span className={styles.tileCaption}>{photo.caption}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * The names behind a photo's subject ids.
 *
 * The wire shape of a subject is `{person_id, is_primary, …}` (docs/05 §8) — the
 * api does not embed a person summary, and it should not: a photo of twelve
 * cousins would carry twelve of them into every gallery page. So the names are
 * resolved here, once each, and cached by react-query across every photo that
 * shares a subject. Showing a uuid fragment to a family member is not an option;
 * these are people, and the point of the tag is their name.
 */
function useSubjectNames(personIds: string[]): Map<string, string> {
  const results = useQueries({
    queries: personIds.map((personId) => ({
      queryKey: ['person-summary', personId],
      queryFn: () => personsApi.get(personId),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const names = new Map<string, string>();
  results.forEach((result, index) => {
    const id = personIds[index];
    if (id && result.data) names.set(id, personName(toPersonSummary(result.data.person)));
  });
  return names;
}

/**
 * The lightbox: the picture, and everything about it that anyone is allowed to
 * change — caption, date, who is in it, and whether it is this person's portrait.
 */
function PhotoLightbox({
  photo,
  subjectPerson,
  onClose,
  onChanged,
  onRemoved,
}: {
  photo: PhotoView;
  subjectPerson: PersonSummary;
  onClose: () => void;
  onChanged: (photo: PhotoView) => void;
  onRemoved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { user } = useSession();
  const [caption, setCaption] = useState(photo.caption ?? '');
  const [taken, setTaken] = useState<FuzzyDateParts | null>(photo.taken);
  const [tagging, setTagging] = useState<PersonSummary | null>(null);
  const names = useSubjectNames(photo.subjects.map((subject) => subject.person_id));

  const mayEdit = user?.role === 'admin' || user?.id === photo.uploader_user_id;

  const save = useMutation({
    mutationFn: () =>
      photosApi.update(photo.id, {
        caption: caption.trim() || null,
        taken: taken && !isFuzzyDateEmpty(taken) ? taken : null,
      }),
    onSuccess: (result) => onChanged(result.photo),
  });

  const tag = useMutation({
    mutationFn: (personId: string) => photosApi.tag(photo.id, personId),
    onSuccess: (result) => onChanged(result.photo),
  });

  const untag = useMutation({
    mutationFn: (personId: string) => photosApi.untag(photo.id, personId),
    onSuccess: () => void photosApi.get(photo.id).then((result) => onChanged(result.photo)),
  });

  const setPortrait = useMutation({
    mutationFn: () =>
      personsApi.update(subjectPerson.id, { changes: { portrait_photo_id: photo.id } as never }),
    onSuccess: () => onChanged(photo),
  });

  const remove = useMutation({
    mutationFn: () => photosApi.remove(photo.id),
    onSuccess: onRemoved,
  });

  return (
    <div
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption ?? t('photos.untitled')}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className={styles.stage}>
        <img src={photo.urls.thumb_1024} alt={photo.caption ?? t('photos.untitled')} />
      </div>

      <aside className={styles.sidebar}>
        <button type="button" className={ui.buttonSecondary} onClick={onClose}>
          {t('photos.close')}
        </button>

        <h2>{photo.caption ?? t('photos.untitled')}</h2>
        <p className={ui.muted}>
          <FuzzyDateText value={photo.taken} />
        </p>

        <h3 className={styles.shelfTitle}>{t('photos.people')}</h3>
        {photo.subjects.length === 0 && <p className={ui.muted}>{t('photos.noSubjects')}</p>}
        {photo.subjects.map((subject) => (
          <div key={subject.person_id} className={styles.subjectRow}>
            <Link to={`/people/${subject.person_id}`}>
              {names.get(subject.person_id) ?? t('photos.loadingName')}
            </Link>
            {subject.is_primary && <span className={ui.muted}>{t('photos.primary')}</span>}
            <span className={styles.spacer} />
            <button
              type="button"
              className={ui.buttonSecondary}
              onClick={() => untag.mutate(subject.person_id)}
            >
              {t('photos.removeTag')}
            </button>
          </div>
        ))}
        {/* Any member may tag — crowdsourcing who is in an old photo is the
            point; removing someone else's tag is not (docs/08 §6.4). */}
        <PersonPicker
          label={t('photos.addPerson')}
          value={tagging}
          onChange={(person) => {
            if (person) tag.mutate(person.id);
            setTagging(null);
          }}
        />

        {mayEdit && (
          <>
            <h3 className={styles.shelfTitle}>{t('photos.details')}</h3>
            <label className={ui.field}>
              <span className={ui.label}>{t('photos.caption')}</span>
              <input
                className={ui.input}
                maxLength={500}
                value={caption}
                onChange={(event) => setCaption(event.target.value)}
              />
            </label>
            <FuzzyDateField legend={t('photos.taken')} value={taken} onChange={setTaken} />
            <button
              type="button"
              className={ui.button}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('photos.save')}
            </button>
            {save.isError && <p className={ui.problem}>{problemMessage(save.error, t)}</p>}

            <button
              type="button"
              className={ui.buttonSecondary}
              onClick={() => setPortrait.mutate()}
            >
              {t('photos.usePortrait', { name: personName(subjectPerson) })}
            </button>
            <button
              type="button"
              className={ui.buttonSecondary}
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {t('photos.delete')}
            </button>
            <p className={ui.muted}>{t('photos.deleteHelp')}</p>
          </>
        )}
      </aside>
    </div>
  );
}
